// FEAT-18 — the candidate's editor settings: text size, theme, word wrap.
//
// ONE store for every editor on the page, because the test page remounts per
// question (App.tsx keys the candidate layout by pathname): a setting kept in
// component state would snap back to the default on every "Next".
//
// Kept in sessionStorage, so it also survives a reload and ends with the tab —
// the same lifetime as the rest of a candidate's state (the attempt id, the
// start nonce), and what the Cookie Policy already says about it. Nothing here
// identifies anyone and nothing is sent to the server. Storage can throw
// (blocked site data, a private window): every access is guarded and the
// editor simply runs on the defaults.
//
// The language is NOT a setting — the recruiter sets it per question (a locked
// decision), and tab size stays the language's own (`AUTHORING[...].tabSize`).

import { useSyncExternalStore } from "react";

export type EditorTheme = "vs-dark" | "vs";

export interface EditorPrefs {
  fontSize: number;
  theme: EditorTheme;
  wordWrap: boolean;
}

export const FONT_SIZE_MIN = 12;
export const FONT_SIZE_MAX = 20;
export const FONT_SIZE_STEP = 2;

export const DEFAULT_EDITOR_PREFS: EditorPrefs = Object.freeze({
  fontSize: 14,
  theme: "vs-dark",
  wordWrap: false,
});

const STORAGE_KEY = "editorPrefs:v1";

/** Whatever was stored (or passed), as settings the editor can take. PURE.
 *  An unknown theme, a size outside the range or off the step, a non-boolean
 *  wrap — each falls back to its own default, never the whole object. */
export function sanitizeEditorPrefs(raw: unknown): EditorPrefs {
  const src = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const size = typeof src.fontSize === "number" && Number.isFinite(src.fontSize)
    ? src.fontSize
    : DEFAULT_EDITOR_PREFS.fontSize;
  const stepped =
    FONT_SIZE_MIN +
    Math.round((size - FONT_SIZE_MIN) / FONT_SIZE_STEP) * FONT_SIZE_STEP;
  return {
    fontSize: Math.min(FONT_SIZE_MAX, Math.max(FONT_SIZE_MIN, stepped)),
    theme: src.theme === "vs" || src.theme === "vs-dark" ? src.theme : DEFAULT_EDITOR_PREFS.theme,
    wordWrap: typeof src.wordWrap === "boolean" ? src.wordWrap : DEFAULT_EDITOR_PREFS.wordWrap,
  };
}

function load(): EditorPrefs {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return raw ? sanitizeEditorPrefs(JSON.parse(raw)) : DEFAULT_EDITOR_PREFS;
  } catch {
    return DEFAULT_EDITOR_PREFS;
  }
}

// Loaded on first read, then the one object every subscriber sees —
// `useSyncExternalStore` needs a stable snapshot between changes.
let current: EditorPrefs | null = null;
const listeners = new Set<() => void>();

export function getEditorPrefs(): EditorPrefs {
  if (current === null) current = load();
  return current;
}

export function setEditorPrefs(patch: Partial<EditorPrefs>): void {
  current = sanitizeEditorPrefs({ ...getEditorPrefs(), ...patch });
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(current));
  } catch {
    // Storage refused: the setting still holds for this page load.
  }
  listeners.forEach((notify) => notify());
}

function subscribe(notify: () => void): () => void {
  listeners.add(notify);
  return () => listeners.delete(notify);
}

export function useEditorPrefs(): EditorPrefs {
  return useSyncExternalStore(subscribe, getEditorPrefs, () => DEFAULT_EDITOR_PREFS);
}

/** Tests only: forget the loaded value so the next read hits storage again. */
export function resetEditorPrefsCache(): void {
  current = null;
}
